import React, { useCallback, useState } from 'react'
import { Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { StatusBar } from 'expo-status-bar'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import Toast from 'react-native-toast-message'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { translate } from '../../i18n'
import { useConfirmDialog } from '../../components/ConfirmDialog'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { ScheduleRepeatSection } from '@/components/ScheduleRepeatSection'
import { SettingsLoadingView, SettingsCenteredMessage } from '@/components/layouts/SettingsScreenLayout'
import { Dropdown } from '../create-scheduled-tournament/Dropdown'
import { MAX_NAME_LENGTH } from '../create-scheduled-tournament/CreateScheduledTournamentForm'
import { LoungeTimePicker } from './LoungeTimePicker'
import { ACTION_TIME_VALUES, BLIND_TIME_VALUES, MINUTE_INTERVAL, STARTING_CHIPS_VALUES } from '@/constants'

// getSchedule returns a union across the schedule tables; not every variant carries these optional
// config/recurrence fields, so read them defensively.
function readOptionalField<T>(schedule: object, key: string, fallback: T): T {
  return (key in schedule ? (schedule as Record<string, T>)[key] : undefined) ?? fallback
}

// Per-game config (blinds, chips, timer) lives in tableConfigs[0] on recurring lounge schedules and
// as top-level scalars on one-off schedules. Prefer tableConfigs, then fall back to the scalar.
function readConfigField(
  schedule: object,
  key: 'blindTimer' | 'startingChips' | 'turnDuration',
  fallback: number
): number {
  const configs = (schedule as { tableConfigs?: Array<Record<string, number | undefined>> }).tableConfigs
  const fromConfig = configs && configs.length > 0 ? configs[0]?.[key] : undefined
  if (typeof fromConfig === 'number') return fromConfig
  return readOptionalField(schedule, key, fallback)
}

// Snap a timestamp to the nearest 15-minute interval so the loaded value lines up with the
// DatePicker's minuteInterval and the user always sees a selectable slot.
function roundToMinuteInterval(timestamp: number): Date {
  const date = new Date(timestamp)
  const roundedMinutes = Math.round(date.getMinutes() / MINUTE_INTERVAL) * MINUTE_INTERVAL
  date.setMinutes(roundedMinutes, 0, 0)
  return date
}

export function LoungeGameSettingsScreen() {
  const router = useRouter()
  const { loungeId: loungeIdStr, scheduleId: scheduleIdStr } = useLocalSearchParams<{
    loungeId: string
    scheduleId: string
  }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const scheduleId = scheduleIdStr as Id<'gameSchedules'> | undefined
  const {
    session: { user }
  } = useStore()
  const userId = user?.auth0Id ?? ''

  const userName = user?.name ?? 'Player'
  const isEditing = Boolean(scheduleId)

  const schedule = useQuery(api.gameSchedules.queries.getSchedule, scheduleId ? { scheduleId } : 'skip')
  const lobby = useQuery(api.lounges.queries.getLoungeLobby, loungeId ? { loungeId, userId } : 'skip')
  const createLoungeScheduledGame = useMutation(api.lounges.mutations.createLoungeScheduledGame)
  const updateSchedule = useMutation(api.gameSchedules.mutations.updateSchedule)
  const deleteSchedule = useMutation(api.gameSchedules.mutations.deleteSchedule)
  const { showConfirm, dialogPortal } = useConfirmDialog()

  const { DEFAULT_BLIND_TIMER_MIN, DEFAULT_STARTING_CHIPS, DEFAULT_ACTION_TIME_SEC, DEFAULT_TURN_DURATION_SEC } =
    GAME_CONSTANTS

  const [gameName, setGameName] = useState('')
  const [blindsLength, setBlindsLength] = useState(String(DEFAULT_BLIND_TIMER_MIN))
  const [startingChips, setStartingChips] = useState(String(DEFAULT_STARTING_CHIPS))
  const [actionTime, setActionTime] = useState(String(DEFAULT_ACTION_TIME_SEC))
  const [startTime, setStartTime] = useState(new Date())
  // New games default to repeating on today's day of the week so they're recurring out of the box;
  // the edit path overrides this from the loaded schedule in the focus effect below.
  const [repeatDays, setRepeatDays] = useState<number[]>(() => (scheduleId ? [] : [new Date().getDay()]))
  const [repeatExpanded, setRepeatExpanded] = useState(false)
  const [saving, setSaving] = useState(false)

  const showErrorToast = (message: string) => {
    Toast.show({
      type: 'error',
      text1: translate('loungeGameSettingsScreen.errorTitle'),
      text2: message
    })
  }

  useFocusEffect(
    useCallback(() => {
      if (!schedule) return
      setGameName(schedule.name)
      setBlindsLength(String(readConfigField(schedule, 'blindTimer', DEFAULT_BLIND_TIMER_MIN)))
      setStartingChips(String(readConfigField(schedule, 'startingChips', DEFAULT_STARTING_CHIPS)))
      setActionTime(String(readConfigField(schedule, 'turnDuration', DEFAULT_ACTION_TIME_SEC)))
      setStartTime(roundToMinuteInterval(schedule.scheduledStartTime))
      setRepeatDays(readOptionalField<number[]>(schedule, 'repeatDaysOfWeekUTC', []))
    }, [schedule])
  )

  const toggleDay = (day: number) => {
    setRepeatDays((days) => (days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b)))
  }

  const validate = (): string | null => {
    if (!gameName.trim()) return translate('loungeGameSettingsScreen.nameRequired')
    if (gameName.length > MAX_NAME_LENGTH) {
      return translate('createTournamentScreen.nameTooLong', { length: MAX_NAME_LENGTH })
    }
    if (repeatDays.length === 0) return translate('loungeGameSettingsScreen.repeatDaysRequired')
    return null
  }

  const gameConfig = () => ({
    blindTimer: Number(blindsLength) || DEFAULT_BLIND_TIMER_MIN,
    startingChips: Number(startingChips.replace(/[^0-9]/g, '')) || DEFAULT_STARTING_CHIPS,
    turnDuration: Number(actionTime) || DEFAULT_TURN_DURATION_SEC
  })

  const saveEdit = async (id: Id<'gameSchedules'>) => {
    // Keep the schedule's date, apply the picked time of day
    const newStart = new Date(schedule?.scheduledStartTime ?? Date.now())
    newStart.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0)
    await updateSchedule({
      scheduleId: id,
      name: gameName.trim(),
      scheduledStartTime: newStart.getTime(),
      ...gameConfig(),
      repeatDaysOfWeekUTC: repeatDays
    })
  }

  const createNew = async (lounge: Id<'lounges'>) => {
    // Build the start time from today, rolling to the next day if the time already passed
    const newStart = new Date()
    newStart.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0)
    if (newStart.getTime() <= Date.now()) {
      newStart.setDate(newStart.getDate() + 1)
    }
    await createLoungeScheduledGame({
      loungeId: lounge,
      userId,
      userName,
      name: gameName.trim(),
      scheduledStartTime: newStart.getTime(),
      ...gameConfig(),
      repeatDaysOfWeekUTC: repeatDays
    })
  }

  const handleSave = async () => {
    const validationError = validate()
    if (validationError) {
      showErrorToast(validationError)
      return
    }

    setSaving(true)
    try {
      if (scheduleId) {
        await saveEdit(scheduleId)
      } else if (loungeId) {
        await createNew(loungeId)
      }
      router.back()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : translate('loungeGameSettingsScreen.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    if (!scheduleId) return
    showConfirm({
      title: translate('loungeGameSettingsScreen.deleteAlert.title'),
      message: translate('loungeGameSettingsScreen.deleteAlert.message'),
      cancelText: translate('loungeGameSettingsScreen.deleteAlert.cancel'),
      confirmText: translate('loungeGameSettingsScreen.deleteAlert.confirm'),
      onConfirm: async () => {
        setSaving(true)
        try {
          await deleteSchedule({ scheduleId })
          if (loungeId) {
            router.replace(`/lounges/${loungeId}`)
          } else {
            router.back()
          }
        } catch (error) {
          showErrorToast(error instanceof Error ? error.message : translate('loungeGameSettingsScreen.deleteFailed'))
        } finally {
          setSaving(false)
        }
      }
    })
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="dark" />
      {dialogPortal}
      <View style={styles.header}>
        <Pressable
          onPress={() => router.back()}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={translate('common.goBack')}
        >
          <DS.Icon name="Arrow---left" size={24} color="Global/Black" />
        </Pressable>
        <View style={styles.headerTitle}>
          <LoungeAvatar name={lobby?.name ?? 'Lounge'} branding={lobby?.branding ?? {}} size={24} />
          <DS.Text variant="Heading 3/Medium" numberOfLines={1} style={styles.headerName}>
            {gameName || schedule?.name || ''}
          </DS.Text>
        </View>
        <View style={styles.headerSpacer} />
      </View>

      {scheduleId && schedule === undefined && <SettingsLoadingView />}
      {scheduleId && schedule === null && (
        <SettingsCenteredMessage message={translate('loungeGameSettingsScreen.notFound')} />
      )}
      {(!scheduleId || schedule != null) && (
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.card}>
            <View style={styles.inputsSection}>
              <View style={styles.field}>
                <DS.Text variant="Body Medium/Regular">{translate('loungeGameSettingsScreen.gameName')}</DS.Text>
                <TextInput style={styles.input} value={gameName} onChangeText={setGameName} />
              </View>
              <View style={styles.fieldRow}>
                <View style={styles.fieldHalf}>
                  <DS.Text variant="Body Medium/Regular">{translate('loungeGameSettingsScreen.blindLength')}</DS.Text>
                  <Dropdown
                    key={`blinds-${blindsLength}`}
                    data={BLIND_TIME_VALUES}
                    defaultValue={`${blindsLength} min`}
                    onSelect={(selectedItem: string) => setBlindsLength(selectedItem.replace(' min', ''))}
                  />
                </View>
                <View style={styles.fieldHalf}>
                  <DS.Text variant="Body Medium/Regular">{translate('loungeGameSettingsScreen.startingChips')}</DS.Text>
                  <Dropdown
                    key={`chips-${startingChips}`}
                    data={STARTING_CHIPS_VALUES}
                    defaultValue={startingChips}
                    onSelect={(selectedItem: string) => setStartingChips(selectedItem)}
                  />
                </View>
              </View>
              <View style={styles.field}>
                <DS.Text variant="Body Medium/Regular">{translate('loungeGameSettingsScreen.actionLength')}</DS.Text>
                <Dropdown
                  key={`action-${actionTime}`}
                  data={ACTION_TIME_VALUES}
                  defaultValue={`${actionTime} sec`}
                  onSelect={(selectedItem: string) => setActionTime(selectedItem.replace(' sec', ''))}
                />
              </View>
              <View style={styles.field}>
                <DS.Text variant="Body Medium/Regular">{translate('loungeGameSettingsScreen.time')}</DS.Text>
                <LoungeTimePicker value={startTime} onChange={setStartTime} />
              </View>
            </View>

            <ScheduleRepeatSection
              repeatDays={repeatDays}
              expanded={repeatExpanded}
              onToggleExpanded={() => setRepeatExpanded((expanded) => !expanded)}
              onToggleDay={toggleDay}
            />
          </View>

          <View style={styles.buttons}>
            <DS.Button
              type="On Light"
              variant="Large/Primary"
              backgroundColor="Brand/Maroon"
              disabled={saving}
              onPress={handleSave}
            >
              {translate('loungeGameSettingsScreen.saveChanges')}
            </DS.Button>
            {isEditing && (
              <DS.Button
                type="On Dark"
                variant="Large/Secondary"
                textColor="Global/Black"
                disabled={saving}
                onPress={handleDelete}
              >
                {translate('loungeGameSettingsScreen.deleteGame')}
              </DS.Button>
            )}
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 8
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1
  },
  headerName: {
    flexShrink: 1
  },
  headerSpacer: {
    width: 24
  },
  content: {
    paddingHorizontal: 24,
    paddingBottom: 24
  },
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 24,
    gap: 24
  },
  inputsSection: {
    gap: 24,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: DS.palette['Global/Black']
  },
  field: {
    gap: 8
  },
  fieldRow: {
    flexDirection: 'row',
    gap: 24
  },
  fieldHalf: {
    flex: 1,
    gap: 8
  },
  input: {
    height: 48,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: DS.palette['Global/Black'],
    paddingHorizontal: 12,
    justifyContent: 'center',
    fontSize: 16,
    color: DS.palette['Global/Black']
  },
  buttons: {
    paddingTop: 32,
    gap: 16
  }
})
